import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { DatabaseContainerType, useAnalytics } from '@databases/analytics';
import Popup from '@databases/atlaskit/Popup';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { ValidationStatusContainer } from '@databases/form/ValidationStatus';
import Z_INDEX from '@databases/layout/z-index';
import { useHoverState, useResizeObserver } from '@databases/react';
import { useHasOpenMenu } from '@databases/react/openMenu';
import { defaultStoreUtils } from '@databases/redux';
import { Adjacents } from '@databases/redux/cellSelections/selectors';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItemContainer, SkeletonItemPlaceholder } from '@databases/skeleton';
import { DatabaseFieldType } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { TABLE_VIEW_HARD_COLUMN_MIN_WIDTH } from '@databases/utils/constants';
import {
  getDatabaseFieldRequired,
  getExistingFieldNames,
  isUniqueFieldName,
} from '@databases/utils/database/utils';
import { ViewChangeTypes } from '@databases/utils/database/view/view';
import { isEmbed } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, useEffect, useMemo, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { InlineEditContainer } from '../../InlineEdit';
import InlineEditWrapper, {
  BorderContainer,
  InlineEditWrapperContainer,
} from '../../InlineEditWrapper';
import { useScope } from '../../Scope';
import FieldSettingsButton, {
  FieldSettingsButtonContainer,
} from '../FieldSettingsButton';

import FieldName from './FieldName';
import FieldNameContainer from './FieldNameContainer';
import { FieldNameEditContainer } from './FieldNameEdit';
import FieldNameInlineEdit from './FieldNameInlineEdit';
import { FieldNameViewContainer } from './FieldNameView';
import SortingAction from './SortingAction';

const Description = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  white-space: pre-line;
  padding: 16px 24px;

  ${FieldNameContainer} {
    font-weight: 600;
    font-size: 12px;
    color: ${token('color.text')};
  }
`;

export const FieldNameInlineContainer = styled.div<{
  hasSorting?: boolean;
  hideText?: boolean;
  isFirstInRow?: boolean;
  isLastInRow?: boolean;
  isSelected?: boolean;
  selectedAdjacentCells?: Adjacents;
}>`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;

  > ${FieldNameContainer} {
    > ${SkeletonItemContainer}:first-child {
      flex-shrink: 0;
      position: absolute;
      left: ${({ isFirstInRow }) => (isFirstInRow ? '20px' : '8px')};
      top: calc(50% - 8px);
      z-index: 1;
      pointer-events: none;
    }

    > ${InlineEditWrapperContainer}:nth-child(2) {
      margin-left: 0;
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;

      > ${ValidationStatusContainer} {
        flex-grow: 1;
        display: flex;

        ${InlineEditContainer} {
          flex-grow: 1;
          display: flex;
          justify-content: center;

          > div {
            padding-right: ${({ hasSorting, isLastInRow }) =>
              hasSorting && isLastInRow
                ? '85px'
                : isLastInRow
                ? '68px'
                : hasSorting
                ? '52px'
                : '30px'};
            justify-content: center;
          }
        }
      }

      // when read only, the input is hidden and the text is shown
      > ${SkeletonItemContainer} {
        flex-grow: 1;
        ${FieldNameViewContainer} {
          height: fit-content;
          align-self: center;
          padding-right: 28px;
        }
      }

      ${FieldNameViewContainer} {
        white-space: pre;
        overflow: hidden;
        display: inline-block;
        text-overflow: ellipsis;
        line-height: 21px;
        ${({ hideText }) =>
          hideText &&
          css`
            display: none;
          `}
      }

      ${FieldNameViewContainer}, ${FieldNameEditContainer} > div > input, ${SkeletonItemPlaceholder} {
        margin-left: ${({ isFirstInRow }) => (isFirstInRow ? '40px' : '28px')}!important;
      }

      > ${BorderContainer} {
        border-bottom: none !important;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;
      }
    }
  }
`;

export const FieldNameWithActionsContainer = styled.div<{
  isSelected?: boolean;
  isSoftSelected?: boolean;
  isIndentedMenu?: boolean;
}>`
  display: flex;
  flex-grow: 1;
  z-index: 1;
  column-gap: 2px;

  &:hover,
  &:focus-within {
    ${FieldSettingsButtonContainer} {
      opacity: 1;
    }
  }

  ${({ isIndentedMenu }) =>
    isIndentedMenu &&
    css`
      > :last-child {
        margin-right: 40px !important;
      }
    `};

  ${({ isSelected, isSoftSelected }) =>
    isSelected &&
    !isSoftSelected &&
    css`
      background-color: ${token('color.background.selected')};
    `};

  * {
    min-width: 0;
  }
`;

export interface FieldNameWithActionsProps {
  contentId: string;
  fieldId: string;
  isReadonly?: boolean;
  couldEditFields?: boolean;
  isDragging?: boolean;
  isSelected?: boolean;
  isSoftSelected?: boolean;
  hoverDeleteButton?: boolean;
  isFirstInRow?: boolean;
  isLastInRow?: boolean;
  selectedAdjacentCells?: Adjacents;
}

/**
 * Renders a field name along with its field actions.
 */
const FieldNameWithActions = forwardRef<HTMLInputElement, FieldNameWithActionsProps>(
  function FieldNameWithActions(
    {
      contentId,
      fieldId,
      isReadonly,
      couldEditFields,
      isDragging = false,
      isSelected,
      isSoftSelected,
      hoverDeleteButton,
      isFirstInRow,
      isLastInRow,
      selectedAdjacentCells,
    },
    inputRef
  ) {
    const { view, setViewChange } = useView();
    const [isHover, hoverProps] = useHoverState();
    const hasOpenMenu = useHasOpenMenu();
    const [fieldNameWithActionsContainer, setFieldNameWithActionsContainer] =
      useState<HTMLDivElement | null>(null);
    const width = useResizeObserver(fieldNameWithActionsContainer)?.width;

    const scope = useScope();

    const [hasSentFieldAnalytics, setHasSentFieldAnalytics] = useState(false);
    const client = useAnalytics();

    const [field, isInvalid, fieldHasValues] = defaultStoreUtils.useEqualSelector(
      (state) => {
        const databaseDraft = selectDatabaseDraftByContentId(state, {
          contentId,
          scope,
          includeDeleted: true,
        });
        const field = getDatabaseFieldRequired(databaseDraft, fieldId);
        const fieldHasValues = Object.values(databaseDraft.entriesById).some(
          (entry) => fieldId in entry.values
        );

        const isInvalid = !isUniqueFieldName(
          field.name,
          getExistingFieldNames(databaseDraft, field)
        );

        return [field, isInvalid, fieldHasValues] as const;
      }
    );

    const containerType = isEmbed()
      ? DatabaseContainerType.EMBED_DATABASE
      : DatabaseContainerType.STANDALONE_DATABASE;

    if (
      !hasSentFieldAnalytics &&
      field.type === DatabaseFieldType.ENTRY_LINK &&
      fieldHasValues &&
      !field.settings.databaseId &&
      !field.settings.contentId
    ) {
      client.sendEntryLinkFieldAnalytics({
        contentId,
        source: 'fieldNameWithActions',
        fieldId: field.id,
        databaseContainer: containerType,
      });
      setHasSentFieldAnalytics(true);
    }

    useEffect(() => {
      if (!field.type || field.type !== DatabaseFieldType.JIRA_ISSUE) {
        return;
      }
      const serverId = field.settings.jiraServerId;
      const cloudId = field.settings.cloudId;
      if (serverId && !cloudId) {
        client.sendDatasourcesDBMigrationConvertIdFailedEvent({
          source: 'FieldNameWithActions',
          missingField: 'cloudId',
          serverId,
          contentId,
          databaseContainer: containerType,
        });
      }
      if (!serverId && cloudId) {
        client.sendDatasourcesDBMigrationConvertIdFailedEvent({
          source: 'FieldNameWithActions',
          missingField: 'serverId',
          cloudId,
          contentId,
          databaseContainer: containerType,
        });
      }
    }, [
      client,
      containerType,
      contentId,
      field.settings.cloudId,
      field.settings.jiraServerId,
      field.type,
    ]);

    const description = useMemo(
      () => (
        <Description>
          <FieldName field={field} />
          <div>{field.description}</div>
        </Description>
      ),
      [field]
    );

    const handleSortChange = useCallback(
      (sort: DatabaseViewSettings['sort']) => {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT,
          sort,
        });
      },
      [setViewChange]
    );

    const sortValue = view.settings.sort;

    const isActiveSortField = useMemo(
      () => field.id === sortValue?.fieldId,
      [field.id, sortValue?.fieldId]
    );

    // if showDescription is undefined, its a database that was made before this feature
    // so they haven't had a chance to toggle it true or false. But we can assume that if
    // there's a field description and showDescription is undefined, then they want it to show
    const showFieldDescription =
      !!field?.description &&
      (field.showDescription || field.showDescription === undefined);

    const shouldHideName = (width || 0) < TABLE_VIEW_HARD_COLUMN_MIN_WIDTH + 20;

    const isCheckboxEnabled = useBooleanFeatureFlag(
      'databases-frontend-checkbox-field-type'
    );
    // if checkbox ff is off, disable field setting
    if (!isCheckboxEnabled && field.type === DatabaseFieldType.CHECKBOX) {
      isReadonly = true;
    }

    return (
      <InlineEditWrapper isInvalid={isInvalid}>
        <Popup
          isOpen={showFieldDescription && !hasOpenMenu && !isDragging && isHover}
          content={() => description}
          placement="bottom"
          trigger={(triggerProps) => (
            <FieldNameWithActionsContainer
              isSelected={isSelected}
              isSoftSelected={isSoftSelected}
              isIndentedMenu={isLastInRow && couldEditFields}
              {...hoverProps}
              {...triggerProps}
              ref={composeRefs(triggerProps.ref, setFieldNameWithActionsContainer)}
            >
              <FieldNameInlineContainer
                hasSorting={isActiveSortField}
                hideText={shouldHideName}
                isFirstInRow={isFirstInRow}
                isLastInRow={isLastInRow}
                isSelected={isSelected}
                selectedAdjacentCells={selectedAdjacentCells}
              >
                <FieldNameInlineEdit
                  fieldId={field.id}
                  showIcon
                  contentId={contentId}
                  isSelected={isSelected}
                  hoverDeleteButton={hoverDeleteButton}
                  isReadonly={isReadonly}
                  selectedAdjacentCells={selectedAdjacentCells}
                  ref={inputRef}
                />
              </FieldNameInlineContainer>
              {!isReadonly && (
                <Inline
                  alignBlock="center"
                  alignInline="center"
                  xcss={xcss({
                    paddingRight: 'space.150',
                  })}
                >
                  <FieldSettingsButton contentId={contentId} fieldId={field.id} />
                  {isActiveSortField && (
                    <SortingAction
                      field={field}
                      onChange={handleSortChange}
                      contentId={contentId}
                    />
                  )}
                </Inline>
              )}
            </FieldNameWithActionsContainer>
          )}
          zIndex={Z_INDEX.TOOLTIP}
          isTooltip
        />
      </InlineEditWrapper>
    );
  }
);

export default FieldNameWithActions;
